import type { RestaurantPromo } from '@/types';

interface Props {
  promo: RestaurantPromo;
}

export default function PromoBand({ promo }: Props) {
  return (
    <div className="relative mb-10 mt-6 lg:mt-4 animate-[promoSway_5s_ease-in-out_infinite]">
      <div
        className={`relative bg-mint border-2 border-ink overflow-hidden grid grid-cols-1 ${promo.photo ? 'md:grid-cols-[210px_1fr_auto]' : 'md:grid-cols-[1fr_auto]'} items-stretch`}
        style={{ boxShadow: 'var(--shadow-stamp)' }}
      >
        {/* diagonal stripe overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'repeating-linear-gradient(45deg, transparent 0 12px, rgba(0,0,0,0.04) 12px 13px)',
          }}
          aria-hidden="true"
        />

        {/* PHOTO column (also anchors the stamp) */}
        {promo.photo && (
          <div className="relative bg-ink/5 border-b-2 md:border-b-0 md:border-r-2 border-ink aspect-[4/3] md:aspect-auto md:min-h-[180px]">
            {/* image clipper — stamp sits OUTSIDE this so it can overflow */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={promo.photo}
                alt=""
                className="absolute inset-0 w-full h-full object-cover animate-[promoZoom_8s_ease-in-out_infinite]"
              />
            </div>
          </div>
        )}

        {/* CONTENT column */}
        <div className="relative z-[1] p-5 md:py-6 md:px-7 flex flex-col justify-center gap-2">
          {/* DF-0141 — cet aplat etait `--sale-red`, une constante `#FE2C55` que
              `applyTheme` ne pose jamais : chez un restaurant a la charte verte
              (bambou) c'etait la seule tache de couleur non maitrisee de la
              page. Il prend maintenant l'accent de la marque, deja themable, et
              son texte suit `--on-accent` au lieu d'un blanc en dur — un accent
              clair rendait ce blanc illisible. `--sale-red` reste le rouge des
              ERREURS (paiement non finalise, etape bloquee) : le thematiser
              rendrait vert un message d'echec. */}
          <span className="self-start inline-flex items-center gap-1.5 bg-hot-pink text-[var(--on-accent)] font-display text-[13px] md:text-[14px] uppercase tracking-[0.08em] px-3 py-1.5 border-2 border-ink shadow-stamp-sm leading-none">
            <span aria-hidden="true" className="animate-[promoFlash_1.4s_ease-in-out_infinite]">⚡</span>
            <span>Offre spéciale · Limitée</span>
          </span>
          <h3 className="font-display text-[var(--on-primary)] leading-[1.05] m-0 text-[24px] md:text-[28px]">
            {promo.headline}
          </h3>
          <p className="font-mono text-[13px] text-[var(--on-primary)] opacity-75 m-0 leading-[1.45]">
            {promo.description}
          </p>
        </div>

        {/* PRICE column */}
        <div className="relative z-[1] px-5 py-3 md:py-6 md:px-7 flex flex-row items-center justify-between gap-3 md:flex-col md:items-end md:justify-center md:gap-1 border-t-2 md:border-t-0 md:border-l-2 border-ink/20 bg-mint/40">
          {promo.originalPriceText && (
            <span className="font-mono text-[13px] md:text-[14px] text-[var(--ink-faint)] line-through leading-none order-2 md:order-none">
              {promo.originalPriceText} €
            </span>
          )}
          <span
            className="font-display text-[var(--on-primary)] leading-[0.9] whitespace-nowrap text-[32px] md:text-[clamp(36px,5.2vw,52px)] order-1 md:order-none"
          >
            {promo.priceText}
            <span className="text-[var(--on-primary)] ml-1 text-[13px] md:text-[0.42em]">€</span>
          </span>
        </div>
      </div>

      {/* Circular discount stamp — anchored to photo column's top-right corner.
          Positioned absolutely against the OUTER wrapper (which doesn't clip),
          using fixed offsets that sit just outside the photo edge. */}
      <div
        className="pointer-events-none absolute z-[5] top-[-22px] right-4 md:right-auto md:left-[178px] animate-[promoStampPop_700ms_cubic-bezier(0.34,1.56,0.64,1)_both]"
      >
        <div className="relative animate-[promoStampWobble_4s_ease-in-out_infinite]">
          {/* outer slow glow ring */}
          <span
            aria-hidden="true"
            className="absolute inset-[-18px] rounded-full bg-hot-pink/25 blur-lg animate-[promoGlowSlow_3200ms_ease-in-out_infinite]"
          />
          {/* inner fast glow */}
          <span
            aria-hidden="true"
            className="absolute inset-[-8px] rounded-full bg-hot-pink/40 blur-md animate-[promoGlowFast_1600ms_ease-in-out_infinite]"
          />
          {/* stamp */}
          <span
            className="relative inline-flex items-center justify-center w-[78px] h-[78px] rounded-full border-2 border-ink font-display text-[var(--on-accent)] text-[22px] leading-none"
            style={{
              background: 'var(--hot-pink)',
              boxShadow: 'var(--shadow-stamp)',
              transform: 'rotate(-10deg)',
            }}
          >
            {promo.tag}
            {/* shimmer sweep */}
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full overflow-hidden"
            >
              <span
                className="absolute top-0 -left-1/2 h-full w-1/2 animate-[promoShimmer_3s_ease-in-out_infinite]"
                style={{
                  background:
                    'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)',
                }}
              />
            </span>
          </span>
        </div>
      </div>

      <style>{`
        @keyframes promoStampWobble {
          0%, 70%, 100% { transform: rotate(0deg) scale(1); }
          75%           { transform: rotate(-10deg) scale(1.10); }
          80%           { transform: rotate(14deg) scale(1.16); }
          85%           { transform: rotate(-5deg) scale(1.06); }
          90%           { transform: rotate(8deg) scale(1.03); }
        }
        @keyframes promoStampPop {
          0%   { opacity: 0; transform: scale(0.2) rotate(-40deg); }
          70%  { opacity: 1; transform: scale(1.18) rotate(6deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes promoGlowFast {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.10); }
        }
        @keyframes promoGlowSlow {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50%      { opacity: 0.65; transform: scale(1.18); }
        }
        @keyframes promoShimmer {
          0%       { transform: translateX(0); }
          60%, 100%{ transform: translateX(420%); }
        }
        @keyframes promoSway {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-2px); }
        }
        @keyframes promoZoom {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.05); }
        }
        @keyframes promoFlash {
          0%, 60%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
          70%           { transform: scale(1.35) rotate(-12deg); opacity: 1; }
          80%           { transform: scale(1.1) rotate(10deg); opacity: 0.9; }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-\\[promoSway_5s_ease-in-out_infinite\\],
          .animate-\\[promoStampWobble_4s_ease-in-out_infinite\\],
          .animate-\\[promoStampPop_700ms_cubic-bezier\\(0\\.34\\,1\\.56\\,0\\.64\\,1\\)_both\\],
          .animate-\\[promoGlowFast_1600ms_ease-in-out_infinite\\],
          .animate-\\[promoGlowSlow_3200ms_ease-in-out_infinite\\],
          .animate-\\[promoShimmer_3s_ease-in-out_infinite\\],
          .animate-\\[promoZoom_8s_ease-in-out_infinite\\],
          .animate-\\[promoFlash_1\\.4s_ease-in-out_infinite\\] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
